import type { StoreApi } from 'zustand';
import { superAdminApi } from '../api/superAdminApi';
import { getAdminApiError } from '../lib/adminApiErrors';
import type {
  AdminApiError,
  AdminBulkFailure,
  AdminBulkResponse,
  AdminLicenseDetailDto,
  AdminCompanyDetailDto,
  AdminCompanyLicenseSummary,
  AdminLicenseDto,
  AdminLicensePatchRequest,
  AdminLicenseSaveResult,
} from '../types';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

type AdminStore = SuperAdminState & SuperAdminActions;
type SetAdminStore = StoreApi<AdminStore>['setState'];
type GetAdminStore = StoreApi<AdminStore>['getState'];
type LicenseActionKeys =
  | 'fetchLicenses'
  | 'loadLicenseDetail'
  | 'clearLicenseDetail'
  | 'createLicense'
  | 'saveLicense'
  | 'deleteLicense'
  | 'bulkLicenses';

const toLicenseListRow = (detail: AdminLicenseDetailDto): AdminLicenseDto => {
  const { machines: _machines, ...row } = detail;
  return row;
};

const upsertLicense = (
  licenses: readonly AdminLicenseDto[],
  detail: AdminLicenseDetailDto
): AdminLicenseDto[] => {
  const row = toLicenseListRow(detail);
  const next = licenses.some((license) => license.id === detail.id)
    ? licenses.map((license) => license.id === detail.id ? row : license)
    : [...licenses, row];
  return next.sort((left, right) => left.id - right.id);
};


const synchronizeCompanyLicense = (
  company: AdminCompanyDetailDto | null,
  detail: AdminLicenseDetailDto
): AdminCompanyDetailDto | null => {
  if (!company) {
    return null;
  }
  const summary: AdminCompanyLicenseSummary = {
    id: detail.id,
    activationKey: detail.activationKey,
    type: detail.type,
    status: detail.status,
    registeredUsersCount: detail.registeredUsersCount,
    allowedRegistrations: detail.allowedRegistrations,
    remainingExportElements: detail.remainingExportElements,
  };
  const existing = company.licenses.some((license) => license.id === detail.id);
  if (company.id !== detail.companyId && !existing) {
    return company;
  }
  const licenses = company.id === detail.companyId
    ? existing
      ? company.licenses.map((license) =>
        license.id === detail.id ? summary : license)
      : [...company.licenses, summary]
    : company.licenses.filter((license) => license.id !== detail.id);
  licenses.sort((left, right) => left.activationKey.localeCompare(right.activationKey));
  return {
    ...company,
    licenses,
    licensesCount: licenses.length,
    licenseKeys: licenses.map((license) => license.activationKey),
  };
};

const fetchLicensesAction = (set: SetAdminStore) => async () => {
  set({ licenseLoading: true, licenseError: null });
  try {
    const licenses = await superAdminApi.getLicenses();
    set({ licenses, licenseLoading: false });
  } catch (error) {
    set({ licenseError: getAdminApiError(error), licenseLoading: false });
  }
};

const loadLicenseDetailAction = (set: SetAdminStore) => async (id: number) => {
  set({ licenseDetail: null, licenseLoading: true, licenseError: null });
  try {
    const licenseDetail = await superAdminApi.getLicense(id);
    set({ licenseDetail, licenseLoading: false });
    return licenseDetail;
  } catch (error) {
    set({ licenseError: getAdminApiError(error), licenseLoading: false });
    throw error;
  }
};

const createLicenseAction: (
  set: SetAdminStore
) => SuperAdminActions['createLicense'] = (set) => async (request) => {
  set({ licenseLoading: true, licenseError: null });
  try {
    const created = await superAdminApi.createLicense(request);
    set((state) => ({
      licenses: upsertLicense(state.licenses, created),
      licenseDetail: created,
      companyDetail: synchronizeCompanyLicense(state.companyDetail, created),
      licenseLoading: false,
    }));
    return created;
  } catch (error) {
    set({ licenseError: getAdminApiError(error), licenseLoading: false });
    throw error;
  }
};

const revokeMachines = async (licenseId: number, machineIds: readonly number[]) => {
  const succeeded: number[] = [];
  const failed: AdminBulkFailure[] = [];
  for (const machineId of machineIds) {
    try {
      await superAdminApi.revokeLicenseMachine(licenseId, machineId);
      succeeded.push(machineId);
    } catch (error) {
      failed.push({ id: machineId, ...getAdminApiError(error, 'license_machine_revoke_failed') });
    }
  }
  return { succeeded, failed };
};

const patchLicense = async (id: number, patch: AdminLicensePatchRequest | null) => {
  if (patch === null) {
    return { succeeded: true, error: null, detail: null };
  }
  try {
    const detail = await superAdminApi.updateLicense(id, patch);
    return { succeeded: true, error: null, detail };
  } catch (error) {
    return {
      succeeded: false,
      error: getAdminApiError(error, 'license_update_failed'),
      detail: null,
    };
  }
};

const reconcileRevokes = (
  detail: AdminLicenseDetailDto | null,
  licenseId: number,
  revokedIds: readonly number[]
): AdminLicenseDetailDto | null => {
  if (!detail || detail.id !== licenseId) {
    return null;
  }
  const revoked = new Set(revokedIds);
  const machines = detail.machines.filter((machine) => !revoked.has(machine.id));
  return { ...detail, machines, machinesCount: machines.length };
};

const addUtcMonths = (value: string | null, months: number): string | null => {
  if (!value) {
    return value;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return value;
  }
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0))
    .getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return date.toISOString();
};

const applyBulkSuccesses = (
  licenses: readonly AdminLicenseDto[],
  action: Parameters<SuperAdminActions['bulkLicenses']>[0],
  succeededIds: readonly number[],
  months: number
): AdminLicenseDto[] => {
  const succeeded = new Set(succeededIds);
  if (action === 'delete') {
    return licenses.filter((license) => !succeeded.has(license.id));
  }
  return licenses.map((license) => {
    if (!succeeded.has(license.id)) {
      return license;
    }
    return action === 'suspend'
      ? { ...license, status: 'suspended' }
      : { ...license, dueTime: addUtcMonths(license.dueTime, months) };
  });
};

const firstSaveError = (
  patchError: AdminApiError | null,
  revokeFailures: AdminBulkFailure[]
): AdminApiError | null => {
  if (patchError) {
    return patchError;
  }
  const failure = revokeFailures[0];
  return failure
    ? { code: failure.code, message: failure.message ?? 'A machine could not be revoked.' }
    : null;
};

const saveLicenseAction: (
  set: SetAdminStore,
  get: GetAdminStore
) => SuperAdminActions['saveLicense'] = (set, get) => async (id, patch, pendingRevokeIds) => {
  set({ licenseLoading: true, licenseError: null });
  const revokes = await revokeMachines(id, pendingRevokeIds);
  const update = await patchLicense(id, patch);
  const fallback = update.detail
    ?? reconcileRevokes(get().licenseDetail, id, revokes.succeeded);
  let canonical = fallback;
  let refreshError: AdminApiError | null = null;
  try {
    canonical = await superAdminApi.getLicense(id);
  } catch (error) {
    refreshError = getAdminApiError(error, 'license_refresh_failed');
  }
  if (!canonical) {
    set({ licenseError: refreshError, licenseLoading: false });
    throw new Error(refreshError?.message ?? 'Canonical license refresh failed.');
  }
  set((state) => ({
    licenses: upsertLicense(state.licenses, canonical),
    licenseDetail: canonical,
    companyDetail: synchronizeCompanyLicense(state.companyDetail, canonical),
    licenseLoading: false,
    licenseError: firstSaveError(update.error, revokes.failed) ?? refreshError,
  }));
  return {
    canonical,
    patchSucceeded: update.succeeded,
    patchError: update.error,
    revokedMachineIds: revokes.succeeded,
    revokeFailures: revokes.failed,
    refreshError,
  } satisfies AdminLicenseSaveResult;
};

const deleteLicenseAction: (
  set: SetAdminStore
) => SuperAdminActions['deleteLicense'] = (set) => async (id) => {
  set({ licenseLoading: true, licenseError: null });
  try {
    await superAdminApi.deleteLicense(id);
    set((state) => ({
      licenses: state.licenses.filter((license) => license.id !== id),
      licenseDetail: state.licenseDetail?.id === id ? null : state.licenseDetail,
    }));
  } catch (error) {
    set({ licenseError: getAdminApiError(error), licenseLoading: false });
    throw error;
  }
  try {
    const licenses = await superAdminApi.getLicenses();
    set({ licenses, licenseLoading: false });
    return { succeeded: true, refreshError: null };
  } catch (error) {
    const refreshError = getAdminApiError(error, 'license_refresh_failed');
    set({ licenseError: refreshError, licenseLoading: false });
    return { succeeded: true, refreshError };
  }
};

const bulkLicensesAction: (
  set: SetAdminStore
) => SuperAdminActions['bulkLicenses'] = (set) => async (action, ids, months) => {
  set({ licenseLoading: true, licenseError: null });
  let result: AdminBulkResponse;
  try {
    result = await superAdminApi.bulkLicenses(action, ids, months);
    set((state) => ({
      licenses: applyBulkSuccesses(state.licenses, action, result.succeeded, months ?? 12),
    }));
  } catch (error) {
    set({ licenseError: getAdminApiError(error), licenseLoading: false });
    throw error;
  }
  try {
    const licenses = await superAdminApi.getLicenses();
    set({ licenses, licenseLoading: false });
    return { ...result, refreshError: null };
  } catch (error) {
    const refreshError = getAdminApiError(error, 'license_refresh_failed');
    set({ licenseError: refreshError, licenseLoading: false });
    return { ...result, refreshError };
  }
};

export const createLicenseStoreActions = (
  set: SetAdminStore,
  get: GetAdminStore
): Pick<SuperAdminActions, LicenseActionKeys> => ({
  fetchLicenses: fetchLicensesAction(set),
  loadLicenseDetail: loadLicenseDetailAction(set),
  clearLicenseDetail: () => set({ licenseDetail: null, licenseError: null }),
  createLicense: createLicenseAction(set),
  saveLicense: saveLicenseAction(set, get),
  deleteLicense: deleteLicenseAction(set),
  bulkLicenses: bulkLicensesAction(set),
});
